import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Input, Select, cn } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import {
  FUNNEL_WRITE_ROLES,
  FUNNEL_STATUS_OPTIONS,
  SEQUENCE_TRIGGER_OPTIONS,
  slugify,
  getFunnelLiveUrl,
} from '../../lib/funnelUtils';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave, FiAlertTriangle } = FiIcons;

export default function FunnelSettings() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    status: 'draft',
    default_entry_step: 1,
    entry_point_step_ids: [],
    sequence_id: '',
    sequence_trigger: 'optin',
    sequence_channels: { email: true, sms: true, ai_caller: false },
  });
  const [slugWarning, setSlugWarning] = useState(false);

  const canWrite = FUNNEL_WRITE_ROLES.includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [funnelData, seqData] = await Promise.all([
        api.funnels.getById(id),
        user?.agency_id ? api.sequences.getAll(user.agency_id) : [],
      ]);
      if (!funnelData) {
        addToast({ type: 'error', title: 'Funnel not found' });
        navigate('/funnels');
        return;
      }
      setFunnel(funnelData);
      setSequences(seqData ?? []);
      setForm({
        name: funnelData.name,
        slug: funnelData.slug,
        status: funnelData.status,
        default_entry_step: funnelData.default_entry_step ?? 1,
        entry_point_step_ids: (funnelData.steps ?? [])
          .filter((s) => s.is_entry_point)
          .map((s) => s.id),
        sequence_id: funnelData.sequence_id ?? '',
        sequence_trigger: funnelData.sequence_trigger ?? 'optin',
        sequence_channels: funnelData.sequence_channels ?? { email: true, sms: true, ai_caller: false },
      });
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load settings', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [id, user?.agency_id, addToast, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      await api.funnels.update(id, {
        name: form.name.trim(),
        slug: form.slug.trim(),
        status: form.status,
        default_entry_step: Number(form.default_entry_step),
        sequence_id: form.sequence_id || null,
        sequence_trigger: form.sequence_trigger,
        sequence_channels: form.sequence_channels,
      });

      const steps = funnel?.steps ?? [];
      for (const step of steps) {
        const isEntry = form.entry_point_step_ids.includes(step.id);
        if (step.is_entry_point !== isEntry) {
          await api.funnelSteps.update(step.id, { is_entry_point: isEntry });
        }
      }

      addToast({ type: 'success', title: 'Settings saved' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Save failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleEntryPoint = (stepId) => {
    setForm((f) => {
      const ids = f.entry_point_step_ids.includes(stepId)
        ? f.entry_point_step_ids.filter((x) => x !== stepId)
        : [...f.entry_point_step_ids, stepId];
      return { ...f, entry_point_step_ids: ids };
    });
  };

  const toggleChannel = (key) => {
    setForm((f) => ({
      ...f,
      sequence_channels: { ...f.sequence_channels, [key]: !f.sequence_channels[key] },
    }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spinner size="lg" className="text-primary" />
      </div>
    );
  }

  const stepOptions = (funnel?.steps ?? []).map((s) => ({
    value: String(s.step_number),
    label: `Step ${s.step_number}: ${s.title}`,
  }));

  const sequenceOptions = [
    { value: '', label: 'None' },
    ...sequences.map((s) => ({ value: s.id, label: s.name })),
  ];

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/funnels/${id}`)}>
          <SafeIcon icon={FiArrowLeft} className="mr-1" />
          Back to Builder
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Funnel Settings</h1>
          <p className="text-sm text-gray-500">{funnel?.name}</p>
        </div>
      </div>

      {!canWrite && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          You have read-only access. Contact a manager to change settings.
        </div>
      )}

      <form onSubmit={handleSave}>
        <Card className="p-6 space-y-5">
          <Input
            label="Funnel name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={!canWrite}
          />

          <div>
            <Input
              label="URL slug"
              value={form.slug}
              onChange={(e) => {
                setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
                setSlugWarning(true);
              }}
              disabled={!canWrite}
            />
            {slugWarning && form.slug !== funnel?.slug && (
              <div className="flex items-start gap-2 mt-2 text-xs text-amber-700 bg-amber-50 rounded-md p-2">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Changing the slug will break existing links pointing to the old URL.</span>
              </div>
            )}
            {form.slug && (
              <p className="text-xs text-gray-500 mt-1 font-mono">{getFunnelLiveUrl(form.slug)}</p>
            )}
          </div>

          <Select
            label="Status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            options={FUNNEL_STATUS_OPTIONS}
            disabled={!canWrite}
          />

          <Select
            label="Default entry step"
            value={String(form.default_entry_step)}
            onChange={(e) => setForm((f) => ({ ...f, default_entry_step: Number(e.target.value) }))}
            options={stepOptions.length ? stepOptions : [{ value: '1', label: 'Step 1' }]}
            disabled={!canWrite}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Entry points</label>
            <p className="text-xs text-gray-500 mb-2">Steps that can be used as direct entry URLs (⚡ in builder).</p>
            <div className="space-y-2">
              {(funnel?.steps ?? []).map((step) => (
                <label key={step.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.entry_point_step_ids.includes(step.id)}
                    onChange={() => toggleEntryPoint(step.id)}
                    disabled={!canWrite}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span>Step {step.step_number}: {step.title}</span>
                </label>
              ))}
            </div>
          </div>

          <hr className="border-gray-100" />

          <Select
            label="Email sequence"
            value={form.sequence_id}
            onChange={(e) => setForm((f) => ({ ...f, sequence_id: e.target.value }))}
            options={sequenceOptions}
            disabled={!canWrite}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sequence trigger</label>
            <select
              value={form.sequence_trigger}
              onChange={(e) => setForm((f) => ({ ...f, sequence_trigger: e.target.value }))}
              disabled={!canWrite}
              className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {SEQUENCE_TRIGGER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {SEQUENCE_TRIGGER_OPTIONS.find((o) => o.value === form.sequence_trigger)?.hint}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sequence channels</label>
            <p className="text-xs text-primary mb-2">Suggested: Email + SMS</p>
            <div className="flex flex-wrap gap-4">
              {[
                { key: 'email', label: 'Email' },
                { key: 'sms', label: 'SMS' },
                { key: 'ai_caller', label: 'AI Caller' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(form.sequence_channels[key])}
                    onChange={() => toggleChannel(key)}
                    disabled={!canWrite}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {canWrite && (
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <Button type="submit" isLoading={saving}>
                <SafeIcon icon={FiSave} className="mr-2" />
                Save Settings
              </Button>
            </div>
          )}
        </Card>
      </form>
    </div>
  );
}
